import { Product } from '@axo/shared/data-access/types';
import { useCallback, useContext, useMemo, useState } from 'react';
import { ApplicationContext } from '../../store/context';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useProductRecommendationReapply } from '@axo/shared/data-access/hooks/services/loan_quote_presentation';
import { useLoanApplication } from '@axo/shared/data-access/hooks/queries/useLoanApplication';
import { useCustomer } from '@axo/shared/data-access/hooks/queries/useCustomer';
import { useLockApplication } from '@axo/shared/data-access/hooks/services/lock/useLockApplication';
import {
  applicationKeys,
  communication_template_hooks,
  customerKeys,
  sms_communication_hooks,
  sms_communication_template_hooks,
} from '@axo/shared/data-access/hooks';
import { convertToE164 } from '@axo/shared/util/formatters';
import {
  KnownResourceType,
  useEventLogger,
} from '@axo/shared/data-access/event-log';
import { AppToaster } from '../../components/AppToaster';
import { getApplicationDataStore } from '../../store/applicationDataStore';

export const useAceProductRecommendationReapply = ({
  applicationID,
  product,
}: {
  applicationID: string;
  product: Product;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { dispatch } = useContext(ApplicationContext);
  const { state } = useContext(DataAccessContext);

  const { data: applicationData } = useLoanApplication(applicationID);
  const { data: customerData } = useCustomer(applicationData?.CustomerID);

  const { data: smsTemplatesData } =
    sms_communication_template_hooks.useSMSTemplates(
      applicationData?.MarketCountry ?? '',
      applicationData?.Meta?.Broker ?? 0,
    );
  const { mutateAsync: fillTemplate } =
    communication_template_hooks.useFillCommunicationTemplate();
  const { mutateAsync: createSMS } =
    sms_communication_hooks.useCreateCommunicationSMS(
      convertToE164({
        phoneNumber: customerData?.MobilePhoneNumber ?? '',
        phoneCountryCode:
          customerData?.MobilePhoneCountryCode?.toString() ?? '',
      }) ?? '',
    );
  const upsellTemplate = smsTemplatesData?.Items.find(
    (template) =>
      template.Active && template.Name.toUpperCase() === 'ACE UPSELL',
  );

  const { mutateAsync: reapplyProduct } = useProductRecommendationReapply();
  const { mutateAsync: acquireLock } = useLockApplication();
  const logger = useEventLogger({
    Type: KnownResourceType.LoanApplication,
    ID: applicationID,
  });

  const [isReapplying, setIsReapplying] = useState(false);

  const reapply = useCallback(async (appliedAmount?: number) => {
    setIsReapplying(true);
    try {
      await logger(`attempt-product-recommendation-reapply: ${product}`);

      const result = await reapplyProduct({
        applicationId: applicationID,
        product,
        channels: [
          {
            CampaignName: 'ace_upsell',
            CampaignMedium: 'ace',
            CampaignSource: 'ace',
          },
        ],
        appliedAmount,
      });

      dispatch({
        type: 'Open application',
        scope: {
          parentType: 'navigation',
          id: result.ID,
        },
        payload: {
          humanReadableID: result.HumanReadableID || result.ID,
          marketCountry: result.MarketCountry,
        },
      });
      navigate(`/application/${result.ID}`);
      await acquireLock({
        marketCountry: result.MarketCountry,
        applicationID: result.ID,
      });
      getApplicationDataStore(result.ID).getState().setIsEditing(true);

      AppToaster.then((toaster) =>
        toaster.show({
          intent: 'success',
          message: `Successfully reapplied. New application ID: ${result.HumanReadableID}`,
        }),
      );
      await logger(`product-recommendation-reapply: ${result.ID}`);

      if (upsellTemplate && applicationData) {
        const filledTemplate = await fillTemplate({
          LoanApplicationID: applicationID,
          LoanQuoteID: '',
          Body: upsellTemplate.Body,
        });

        const toCreate = {
          LoanApplicationID: applicationID,
          MarketCountry: applicationData.MarketCountry,
          CreatorID: state.user.clientID,
          CustomerID: applicationData.CustomerID,
          TemplateID: upsellTemplate.ID,
          Transactional: upsellTemplate.Transactional,
          LoanQuoteID: '',
          Body: filledTemplate.Body,
          CustomerPhoneNumber:
            convertToE164({
              phoneNumber: customerData?.MobilePhoneNumber ?? '',
              phoneCountryCode:
                customerData?.MobilePhoneCountryCode?.toString() ?? '',
            }) ?? '',
          Source: 'ACE',
          Meta: {
            Broker: applicationData?.Meta?.Broker,
            Brand: applicationData?.Meta?.Brand,
          },
        };

        await createSMS(toCreate);
      }
    } catch (e) {
      if (e instanceof Error) {
        AppToaster.then((toaster) =>
          toaster.show({
            intent: 'danger',
            message: e.message,
          }),
        );
      }
    } finally {
      // Wait a bit to hopefully let ace index catchup
      await new Promise((resolve) => setTimeout(resolve, 5000));

      queryClient.removeQueries(
        customerKeys.loanApplications({
          customerID: applicationData?.CustomerID,
        }),
      );

      queryClient.removeQueries(
        applicationKeys.productRecommendation({ applicationID }),
      );

      setIsReapplying(false);
    }
  }, [
    applicationID,
    reapplyProduct,
    product,
    logger,
    applicationData,
    queryClient,
    fillTemplate,
    upsellTemplate,
    createSMS,
    customerData?.MobilePhoneCountryCode,
    customerData?.MobilePhoneNumber,
    state.user.clientID,
    dispatch,
    navigate,
    acquireLock,
  ]);

  return useMemo(
    () => ({
      reapply,
      isReapplying,
    }),
    [reapply, isReapplying],
  );
};
